import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly _counters = new Map<string, WritableSignal<number>>();
  private readonly _signals  = new Map<string, Signal<boolean>>();

  // Backwards-compatible — true when the 'global' context has active requests
  readonly isLoading: Signal<boolean>;

  constructor() {
    this._getOrCreateCounter('global');
    this.isLoading = this.isLoadingFor('global');
  }

  /**
   * Returns a readonly signal that is true while the given context has active requests.
   * Signals are cached — the same instance is returned for the same key.
   */
  isLoadingFor(key: string): Signal<boolean> {
    if (!this._signals.has(key)) {
      const counter = this._getOrCreateCounter(key);
      this._signals.set(key, computed(() => counter() > 0));
    }
    return this._signals.get(key)!;
  }

  show(key = 'global'): void {
    this._getOrCreateCounter(key).update(n => n + 1);
  }

  hide(key = 'global'): void {
    this._getOrCreateCounter(key).update(n => Math.max(0, n - 1));
  }

  forceHide(key?: string): void {
    if (key) {
      this._counters.get(key)?.set(0);
    } else {
      this._counters.forEach(counter => counter.set(0));
    }
  }

  private _getOrCreateCounter(key: string): WritableSignal<number> {
    if (!this._counters.has(key)) {
      this._counters.set(key, signal(0));
    }
    return this._counters.get(key)!;
  }
}
